import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { PLATFORM_TENANT_SLUG } from "@/constants/platform";
import {
  readTenantProfileCache,
  resolveEffectiveTenantSlug,
  resolveTenantFromHost,
  writeTenantProfileCache,
} from "@/utils";
import { tenantsService } from "@/services";
import type { TenantPublicResponse } from "@/types";
import { TenantContext, type TenantContextValue } from "./tenant.context";
import { TenantDocumentMeta } from "./TenantDocumentMeta";

interface TenantProviderProps {
  children: React.ReactNode;
}

const BASE_DOMAIN =
  import.meta.env.VITE_BASE_DOMAIN ?? "tercerespacio.com.ar";

const readInitialTenantProfile = (): TenantPublicResponse | null =>
  readTenantProfileCache(resolveEffectiveTenantSlug(window.location.hostname));

/**
 * Resolves tenant from hostname and loads public tenant profile
 */
export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const resolution = useMemo(
    () => resolveTenantFromHost(window.location.hostname, BASE_DOMAIN),
    [],
  );

  const effectiveTenantSlug = useMemo(
    () => resolveEffectiveTenantSlug(window.location.hostname),
    [],
  );

  const [tenant, setTenant] = useState<TenantPublicResponse | null>(() =>
    readInitialTenantProfile(),
  );
  const [loading, setLoading] = useState(() => !readInitialTenantProfile());
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    const cachedProfile = readTenantProfileCache(effectiveTenantSlug);

    const loadTenant = async () => {
      if (!cachedProfile) {
        setLoading(true);
      }
      setError(null);

      try {
        const data = await tenantsService.getPublicBySlug(effectiveTenantSlug);
        if (!cancelled) {
          setTenant(data);
          writeTenantProfileCache(effectiveTenantSlug, data);
        }
      } catch (err) {
        if (!cancelled) {
          setTenant(null);
          setError(
            err instanceof Error
              ? err
              : new Error("No se pudo cargar el tenant"),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadTenant();

    return () => {
      cancelled = true;
    };
  }, [effectiveTenantSlug]);

  const value = useMemo<TenantContextValue>(
    () => ({
      isTenantSite: resolution !== null,
      tenantSlug: resolution?.slug ?? null,
      effectiveTenantSlug,
      isMainTenantHost: effectiveTenantSlug === PLATFORM_TENANT_SLUG,
      isPlatform: tenant?.isPlatform ?? effectiveTenantSlug === PLATFORM_TENANT_SLUG,
      tenant,
      space: tenant?.space ?? null,
      loading,
      error,
    }),
    [effectiveTenantSlug, error, loading, resolution, tenant],
  );

  return (
    <TenantContext.Provider value={value}>
      <TenantDocumentMeta />
      {children}
    </TenantContext.Provider>
  );
};
